import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const ServiceContext = createContext();

export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
};

export const ServiceProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [services, setServices] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/services');

  const getServices = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('services?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('services');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setServices(response.data.services || []);
       
        
        if(filters){
          return response.data.services;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch services information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getServiceByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`services/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let services = {'data':[response.data.service]}
      setServices(services || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/services/edit/'+id))){
        if (getToken()) {
         
          await getServiceByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getServiceByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getServices(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getServices, searchParams, initialized,hideLoading,setApi]);

  const getServiceAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-services-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get service data'
      });
    } 
  }, [getToken, handleApiError]);

  const addService = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append images (files)
      if (data.images) {
        data.images.forEach((item, index) => {
          formData.append(`images[${index}][id]`, item.id);
          formData.append(`images[${index}][file]`, item.file);
          formData.append(`images[${index}][name]`, item.name);
          formData.append(`images[${index}][size]`, item.size);
          formData.append(`images[${index}][type]`, item.type);
          formData.append(`images[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove images from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.images;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('services', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the services list
        await getServices();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Service'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create service'
      });
      return { status: 500, error: error.message };
    }
  };

  const getService = (id) => {
    return services?.data?.find(service => Number(service.id) === Number(id));
  };

  const getServiceById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`services/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.service };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch service details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch service details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateService = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append images (files)
      if (updatedData.images) {
        updatedData.images.forEach((item, index) => {
          formData.append(`images[${index}][id]`, item.id);
          formData.append(`images[${index}][file]`, item.file);
          formData.append(`images[${index}][name]`, item.name);
          formData.append(`images[${index}][size]`, item.size);
          formData.append(`images[${index}][type]`, item.type);
          formData.append(`images[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove images from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.images;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`services/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the services list
        await getServices();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Service'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update service'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteService = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`services/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the services list
        await getServices();
        navigate(getRoutePath('/services'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete service'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete service'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <ServiceContext.Provider
      value={{
        services,
        addService,
        getService,
        getServiceById,
        updateService,
        deleteService,
        getServices,
        getServiceAdditionalData,
        formErrors,
        clearErrors
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export default ServiceContext; 